import axios from 'axios'
import hash from 'object-hash'
import { identity, omit } from 'ramda'

import { mergeDeepAll } from '../utils'
import {
  defaultMinMaxIndicators,
  defaultMSDIndicators,
  range6m1yAll,
} from '../utils/config'
import { DEFAULT_TIMEOUT_MS } from '../utils/constants'
import { CommonChartConfig, commonChartOptions } from './common'
import type { BaseContext, RequestModifier } from './'

interface BaseBasketConfig {
  basket_id: string
  basket_username: string
  weights_id: string
  metric: string
  metric_args: Record<string, (string | number[]) | null>
  sectors: boolean
  table: boolean
  wait_until_response: boolean
  use_cache?: boolean
  max_cache_delay?: number
}

interface ValuationBasketConfig extends BaseBasketConfig {
  start_date: string
  end_date: string
  only_when_all_tickers_exist: boolean
  only_tickers_in_all_history: boolean
}

interface SurpriseBasketConfig extends BaseBasketConfig {}

interface BaseBasketRequest {
  requestId: string
  name: string
  metric: string
}

interface ValuationBasketRequest extends BaseBasketRequest {
  metricType: 'valuation' | 'estimates'
  settings: ValuationBasketConfig
}

interface SurprisesBasketRequest extends BaseBasketRequest {
  metricType: 'surprises'
  settings: SurpriseBasketConfig
}

type BasketRequest = ValuationBasketRequest | SurprisesBasketRequest

const BASKET_RATIO_URL = '/api/valuation/ratio-baskets'
const COMPASS_BASE_URL = 'https://compass-esg.jgp.com.br'

const getBasketRequestUrl = (
  metricType: 'valuation' | 'estimates' | 'surprises'
) =>
  metricType === 'surprises'
    ? '/api/valuation/basket-surprise-request'
    : '/api/valuation/basket-metric-request'

const getChartUrl = (config: { shareId?: string | null }) =>
  config.shareId
    ? `${COMPASS_BASE_URL}/valuation/?settingsId=${config.shareId}`
    : `${COMPASS_BASE_URL}/valuation`

export namespace Valuation {
  export namespace Metric {
    const CONTEXT_ID = 'equity:valuation:metric'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    interface BaseChartConfig extends CommonChartConfig {
      requestId: string
      updateUrl: string
      title: string
      context: typeof CONTEXT_ID
      shareId?: string | null
    }

    export interface ValuationChartConfig extends BaseChartConfig {
      metricType: 'valuation' | 'estimates'
      settings: ValuationBasketConfig
    }

    interface SurprisesChartConfig extends BaseChartConfig {
      metricType: 'surprises'
      settings: SurpriseBasketConfig
    }

    export type ChartConfig = ValuationChartConfig | SurprisesChartConfig

    interface ValuationChartData {
      data: {
        id: string
        name: string
        decimals: number
        unit: string
        values: {
          date: string
          value: number
        }[]
      }[]
    }

    interface SurprisesChartData {
      data: {
        id: string
        title: string
        x_decimals: number
        x_unit: string
        y_decimals: number
        y_unit: string
        values: {
          x_value: number
          y_value: number
          label: string
        }[]
      }[]
    }

    export type ChartData = ValuationChartData | SurprisesChartData

    const getChartData = async (
      config: ChartConfig,
      requestModifier: RequestModifier = identity
    ) => {
      const {
        data: { request_id: requestId },
      } = await axios(
        requestModifier({
          url: getBasketRequestUrl(config.metricType),
          method: 'POST',
          data: {
            ...config.settings,
            use_cache: true,
            max_cache_delay: 30,
          },
        })
      )

      const response = await axios(
        requestModifier({
          url: config.url,
          method: 'GET',
          params: { request_id: requestId },
          timeout: DEFAULT_TIMEOUT_MS,
        })
      )
      return response.data
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.requestId}`,
      getChartData,
      getChartProps: (config, data) =>
        config.metricType === 'surprises'
          ? getSurprisesChartProps(config, data as SurprisesChartData)
          : getValuationChartProps(config, data as ValuationChartData),
      getChartUrl,
    }

    const getSurprisesChartProps = (
      config: SurprisesChartConfig,
      data: SurprisesChartData
    ) => {
      const x_decimals = data.data[0]?.x_decimals ?? 2
      const x_unit = data.data[0]?.x_unit ?? ''
      const y_decimals = data.data[0]?.y_decimals ?? 2
      const y_unit = data.data[0]?.y_unit ?? ''

      const series = data.data.map((s, idx) => ({
        id: `series-${idx}`,
        name: s.title,
        type: 'bubble',
        yAxis: 'default',
        data: s.values.map(({ x_value, y_value, label }) => ({
          x: x_value,
          y: y_value,
          label,
        })),
        dataLabels: {
          enabled: true,
          align: 'left',
          inside: false,
          x: 10,
          y: 5,
          format: '{point.label}',
        },
        minSize: 10,
        maxSize: 10,
        tooltip: {
          pointFormat: `{point.label}: ({point.x:.${x_decimals}f}${x_unit}, {point.y:.${y_decimals}f}${y_unit})`,
        },
      }))

      const xAxis = [{ labels: { format: `{value}${x_unit}` } }]

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: '',
          labels: { format: `{value}${y_unit}` },
          startOnTick: false,
          endOnTick: false,
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title,
        options: {
          ...commonChartOptions,
          chart: {
            zoomType: 'xy',
          },
          credits: { text: '' },
          series,
          tooltip: {
            split: false,
          },
          xAxis,
          yAxis,
        },
        useRegularChart: true,
      }
    }

    const getValuationChartProps = (
      config: ValuationChartConfig,
      data: ValuationChartData
    ) => {
      const series = data.data.map((s) => ({
        id: s.id,
        type: 'line',
        name: s.name,
        yAxis: 'default',
        data: s.values.map(({ date, value }) => [
          new Date(date).getTime(),
          value,
        ]),
        tooltip: {
          valueDecimals: s.decimals,
          valueSuffix: s.unit,
        },
      }))

      const yAxis = [
        { id: 'default', opposite: true, startOnTick: false, endOnTick: false },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title,
        defaultIndicators: [
          ...defaultMinMaxIndicators(series),
          ...(data.data.length === 1
            ? defaultMSDIndicators(series, { avg: true, sd1: true, sd2: true })
            : []),
        ],
        options: {
          ...commonChartOptions,
          credits: { text: '' },
          rangeSelector: range6m1yAll,
          series,
          yAxis,
        },
        stockTools: true,
      }
    }
  }

  export namespace Ratio {
    const CONTEXT_ID = 'equity:valuation:ratio'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    interface ChartConfig {
      context: typeof CONTEXT_ID
      basket1: BasketRequest
      basket2: BasketRequest
      shareId?: string | null
      title?: string
    }

    interface ChartData {
      id: string
      name: string
      first_metric: {
        date: string
        value: number
      }[]
      second_metric: {
        date: string
        value: number
      }[]
      ratio: {
        date: string
        value: number
      }[]
      spread: {
        date: string
        value: number
      }[]
    }

    const getChartData = async (
      config: ChartConfig,
      requestModifier: RequestModifier = identity
    ) => {
      const makeBasketRequest = async (basket: BasketRequest) =>
        requestModifier({
          url: getBasketRequestUrl(basket.metricType),
          method: 'POST',
          data: {
            ...basket.settings,
            use_cache: true,
            max_cache_delay: 30,
          },
        })

      await Promise.all([
        makeBasketRequest(config.basket1),
        makeBasketRequest(config.basket2),
      ])

      return axios(
        requestModifier({
          url: BASKET_RATIO_URL,
          method: 'POST',
          data: [config.basket1.requestId, config.basket2.requestId],
        })
      ).then((res) => res.data.data[0])
    }

    const parser = (point: { date: string; value: number }) => [
      new Date(point.date).getTime(),
      point.value,
    ]

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const ratioSeries = [
        {
          id: 'basket-ratio',
          type: 'line',
          name: 'Ratio',
          yAxis: 'ratio',
          tooltip: { valueDecimals: 3 },
          data: data.ratio.map((point) => parser(point)),
        },
      ]

      const metricSeries = [config.basket1, config.basket2].map(
        (basket, idx) => ({
          id: `basket-${idx}`,
          type: 'line',
          name: basket.name,
          yAxis: 'metric',
          tooltip: { valueDecimals: 2 },
          data:
            idx === 0
              ? data.first_metric.map((point) => parser(point))
              : data.second_metric.map((point) => parser(point)),
        })
      )

      const yAxis = [
        {
          id: 'ratio',
          opposite: false,
          height: '60%',
          resize: { enabled: true },
          startOnTick: false,
          endOnTick: false,
        },
        {
          id: 'metric',
          opposite: false,
          height: '40%',
          top: '60%',
          offset: 0,
          resize: { enabled: true },
          startOnTick: false,
          endOnTick: false,
        },
      ]

      return {
        id: Context.getChartId(config),
        title: `Ratio ${config.basket1.name} / ${config.basket2.name}`,
        height: '450px',
        defaultIndicators: [
          ...(config.basket1.requestId !== config.basket2.requestId
            ? defaultMinMaxIndicators(ratioSeries)
            : []),
          ...defaultMinMaxIndicators(metricSeries),
          ...defaultMSDIndicators(ratioSeries, {
            avg: true,
            sd1: true,
            sd2: true,
          }),
        ],
        options: mergeDeepAll([
          commonChartOptions,
          {
            credits: { text: '' },
            series: [...ratioSeries, ...metricSeries],
            yAxis,
          },
        ]),
        stockTools: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${hash(omit(['shareId'], config))}`,
      getChartData,
      getChartProps,
      getChartUrl,
    }
  }
}
